import { Menu, Transition } from "@headlessui/react";
import { ChevronDoubleLeftIcon } from "@heroicons/react/20/solid";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { defaultOrganizationFor } from "@via/hooks/organization-access.ts";
import {
  CREATE_DROPDOWN,
  WORKBENCH_CREATE_DROPDOWN,
  NAVIGATION,
  WORKBENCH_NAVIGATION,
  AGENT_WORKSPACES_NAVIGATION,
  CORTEX_NAVIGATION,
  ACTIVITY_NAVIGATION,
  SCAN_NAVIGATION,
} from "@via/utils/constants.tsx";
import { isScanAvailable, withScanNavigation } from "@via/utils/scan";
import {
  classNames,
  isCurrent,
  shouldBlockAccess,
  shouldBlockOrgMemberAccess,
} from "@via/utils/helpers.tsx";
import { Fragment, useMemo, type JSX } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "./navigation.css";
import Logo from "/logo.svg";
import { Can } from "@casl/react";
import { useAbility } from "@via/context/ability-context";
import {
  useUserConfigurations,
  useConfigurations,
} from "@via/api/configuration";

export default function SideNavigation(): JSX.Element {
  const { sidebarExpanded, setSidebarExpanded, selectedOrganization } =
    useGlobal();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { data: configurations } = useUserConfigurations();
  const { data: mainConfigurations } = useConfigurations();
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: false,
    },
  ]);
  const expandSidebar = (): void => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const navigationOptions = useMemo(() => {
    let options = [...NAVIGATION];

    if (user && configurations) {
      // The organization last worked in, not whichever membership sorts first (#1069).
      const organizationId =
        defaultOrganizationFor(user, selectedOrganization?.id)?.id || "";
      const isOrganizationActive = organizationId !== "";
      if (
        user.registrationType !== null &&
        organizationId &&
        isOrganizationActive
      ) {
        const ORGANIZATION_NAVIGATION = {
          name: "Organization",
          href: `/organization/${organizationId}`,
          icon: BuildingOfficeIcon,
        };
        options = [ORGANIZATION_NAVIGATION, ...options];
      } else {
        options = [ACTIVITY_NAVIGATION, ...options];
      }

      if (configurations.AGENT_WORKSPACE_JOBS_ENABLED) {
        options = [...options, AGENT_WORKSPACES_NAVIGATION];
      }

      if (configurations.SHOW_WORKBENCH) {
        options = [...options, WORKBENCH_NAVIGATION];
      }

      if (user?.featureFlags?.FF_MODEL_SMITH) {
        options = [...options, CORTEX_NAVIGATION];
      }
    }
    return withScanNavigation(
      options,
      SCAN_NAVIGATION,
      isScanAvailable(mainConfigurations)
    );
  }, [configurations, mainConfigurations, user, selectedOrganization?.id]);

  const createDropdownOptions = useMemo(() => {
    if (configurations && configurations.SHOW_WORKBENCH) {
      return [...CREATE_DROPDOWN, WORKBENCH_CREATE_DROPDOWN];
    }
    return CREATE_DROPDOWN;
  }, [configurations]);

  const blockAccess = shouldBlockAccess(user);
  const blockOrgMemberAccess = shouldBlockOrgMemberAccess(user);
  const shouldShowHeader = Boolean(
    user?.session && !blockAccess && !blockOrgMemberAccess
  );

  return (
    <>
      {shouldShowHeader && (
        <div
          id="side-navigation"
          className={classNames(
            sidebarExpanded ? "lg:w-60" : "lg:w-16",
            "sticky flex h-auto shrink-0 border-r border-gray-200 duration-150 ease-in-out max-lg:hidden lg:inset-y-0 lg:z-50 lg:h-screen"
          )}>
          <nav className="flex grow flex-col justify-between overflow-y-auto overflow-x-hidden bg-slate-50 px-[11.5px] py-4">
            <ul role="list" className="space-y-1 duration-150 ease-in-out">
              <li className="flex px-1 pb-5">
                <Link className="tooltip" to="/activity">
                  <img
                    className="h-8 cursor-pointer duration-200 ease-in-out hover:rotate-180"
                    src={Logo}
                    alt="Foundry Connect logo"
                  />
                </Link>
              </li>
              {/* Map the Items for Left Side Navigation */}
              <li>
                <Menu as="a" className="flex duration-150 ease-in-out">
                  <Menu.Button
                    id="create"
                    className="group inline-flex w-full items-center pb-2 pl-1 duration-150 ease-in-out">
                    <PlusIcon
                      className="h-8 w-8 shrink-0 rounded-full bg-indigo-600 p-1 text-white shadow-md duration-150 ease-in-out hover:bg-indigo-500"
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        sidebarExpanded ? "block" : "",
                        "whitespace-nowrap pl-4 text-base font-semibold leading-6 text-indigo-600 duration-150 ease-in-out hover:text-indigo-500"
                      )}>
                      Create
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95">
                    <Menu.Items className="absolute z-10 mt-11 w-[232px] origin-top-right divide-y divide-gray-100 rounded-md bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {createDropdownOptions.map(item => {
                        if (
                          item.name === "New App" ||
                          item.name === "New Workbench"
                        ) {
                          return (
                            <Can
                              I="View"
                              a="Button"
                              ability={ability}
                              key={item.name}>
                              {ability.can("View", "Button") && (
                                <Menu.Item key={item.name}>
                                  <Link
                                    to={item.href}
                                    className="flex flex-row items-center border-none px-3 py-2 text-sm leading-6 text-gray-700 hover:bg-gray-100">
                                    <item.icon
                                      className="mr-3 h-5 w-5 shrink-0 duration-150 ease-in-out"
                                      aria-hidden="true"
                                    />
                                    {item.name}
                                  </Link>
                                </Menu.Item>
                              )}
                            </Can>
                          );
                        }
                        return (
                          <Menu.Item key={item.name}>
                            <Link
                              to={item.href}
                              className="flex flex-row items-center border-none px-3 py-2 text-sm leading-6 text-gray-700 hover:bg-gray-100">
                              <item.icon
                                className="mr-3 h-5 w-5 shrink-0 duration-150 ease-in-out"
                                aria-hidden="true"
                              />
                              {item.name}
                            </Link>
                          </Menu.Item>
                        );
                      })}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </li>
              {navigationOptions.map(item => (
                <li key={item.name}>
                  <Link
                    id={`side-nav-${item.name.toLowerCase()}`}
                    to={item.href}
                    className={classNames(
                      isCurrent(item.href, pathname)
                        ? "bg-gray-100 text-indigo-600 duration-200 ease-in-out hover:bg-gray-200"
                        : "text-gray-700 hover:bg-gray-100 hover:text-indigo-600",
                      "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 duration-150 ease-in-out"
                    )}>
                    <item.icon
                      id={item.name}
                      className={classNames(
                        isCurrent(item.href, pathname)
                          ? "text-indigo-600"
                          : "text-gray-400 group-hover:text-indigo-600",
                        "h-6 w-6 shrink-0 duration-150 ease-in-out"
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        sidebarExpanded ? "block" : "",
                        "whitespace-nowrap pl-2 duration-150 ease-in-out"
                      )}>
                      {item.name}
                    </span>
                  </Link>
                  {!sidebarExpanded && (
                    <Tooltip
                      anchorSelect={`#${CSS.escape(item.name)}`}
                      content={item.name}
                      place="right"
                      offset={14}
                    />
                  )}
                </li>
              ))}
            </ul>
            <div className="collapse-sidebar gap-x-3 self-end p-2 pt-2.5 duration-150 ease-in-out">
              <ChevronDoubleLeftIcon
                onClick={() => {
                  expandSidebar();
                }}
                className={classNames(
                  sidebarExpanded ? "rotate-0" : "rotate-180",
                  "h-7 w-7 cursor-pointer justify-between whitespace-nowrap text-gray-400 duration-150 ease-in-out hover:text-indigo-600"
                )}
              />
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
